import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { LogOut, User, Building, Mail, Phone, Globe, Award, Save, ArrowLeft, Plus, X, Star, Calendar, MapPin, Globe2, Clock, Hotel, ShoppingCart, CreditCard, Upload, FileText, Shield, Trash2, DollarSign, Settings, CheckCircle, XCircle, BookOpen, ChevronRight } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import uploadService from '../services/uploadService'
import LoadingSpinner from '../components/LoadingSpinner'
import MainLayout from '../components/Layout/MainLayout'
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage'
import AgreementPopup from '../components/AgreementPopup' 
import TermsPopup from '../components/TermsPopup' 

const ProfileManagementPage = () => {
  const [profile, setProfile] = useState(null)
  const [formData, setFormData] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [newCertification, setNewCertification] = useState('')
  const [newSpecialization, setNewSpecialization] = useState('')
  const [newTargetMarket, setNewTargetMarket] = useState('')
  const [uploadingProof, setUploadingProof] = useState(false)
  const [uploadingPhotoId, setUploadingPhotoId] = useState(false)
  const [proofProgress, setProofProgress] = useState(0)
  const [photoIdProgress, setPhotoIdProgress] = useState(0)
  const navigate = useNavigate()
  const [mounted, setMounted] = useState(false)
  const location = useLocation()
  const [showAgreementPopup, setShowAgreementPopup] = useState(false)
  const [agreementStatus, setAgreementStatus] = useState(profile?.agreementAccepted)

  const [showTermsPopup, setShowTermstPopup] = useState(false)
  useEffect(() => {
    loadProfile()
  }, [])

  useEffect(() => {
    setMounted(true)
  }, [])

const handleAgreementButtonClick = () => {
  // Use the profile data which is updated after loadProfile()
  if (profile?.agreementAccepted) {
    // If already accepted, show view mode
    setShowAgreementPopup(true);
  } else {
    // Show agreement submission popup
    setShowAgreementPopup(true);
  }
}


 const handleGeneralTermsButtonClick = () => {
  setShowTermstPopup(true)
  // if (agreementStatus) {
  //   // If already accepted, show view mode
  //   // You can implement a view-only modal here
  //   console.log('View agreement')
  // } else {
  //   // Show agreement submission popup
  //   setShowTermstPopup(true)
  // }
}

const handleAgreementSubmit = async (agreementData, signatureFile) => {
  try {
    setIsSaving(true);
    
    // Prepare form data
    const formData = new FormData();
    formData.append('contactPerson', agreementData.contactPerson);
    formData.append('city', agreementData.city);
    formData.append('country', agreementData.country);
    formData.append('companyAddress', agreementData.companyAddress);
    formData.append('companyName', agreementData.companyName);
    formData.append('registrationNumber', agreementData.registrationNumber);
    formData.append('ipAddress', agreementData.ipAddress);
    formData.append('dateOfAcceptance', agreementData.dateOfAcceptance);
    formData.append('signatureType', agreementData.signatureType);
    formData.append('signatureData', JSON.stringify(agreementData.signatureData));
    
    if (signatureFile) {
      formData.append('signatureImage', signatureFile);
    }

    // Call your API service
    const response = await travelAgentService.submitAgreement(formData);
    
    if (response.success) {
      // Update local state immediately
      setAgreementStatus(true);
      setMessage({ type: 'success', text: 'Agreement accepted successfully!' });
      
      // Refresh profile data to get latest from server
      await loadProfile();
    }
    
    return response;
  } catch (error) {
    console.error('Error submitting agreement:', error);
    setMessage({ type: 'error', text: 'Failed to submit agreement' });
    throw error;
  } finally {
    setIsSaving(false);
  }
}
  const loadProfile = async () => {
    try {
      const response = await travelAgentService.getProfile()
      setProfile(response.data)
      setFormData({
        name: response.data.name || '',
        phoneNumber: response.data.phoneNumber || '',
        country: response.data.country || '',
        businessType: response.data.businessType || '',
        legalBusinessName: response.data.legalBusinessName || '',
        businessAddress: response.data.businessAddress || '',
        businessRegistrationNumber: response.data.businessRegistrationNumber || '',
        taxIdentificationNumber: response.data.taxIdentificationNumber || '',
        iataCliaArcNumber: response.data.iataCliaArcNumber || '',
        businessLicense: response.data.businessLicense || '',
        proofOfBusinessUrl: response.data.proofOfBusinessUrl || '',
        photoIdUrl: response.data.photoIdUrl || '',
        businessPhoneNumber: response.data.businessPhoneNumber || '',
        businessEmail: response.data.businessEmail || '',
        preferredContactMethod: response.data.preferredContactMethod || 'email',
        businessHours: response.data.businessHours || '',
        specializations: response.data.specializations || [],
        targetMarkets: response.data.targetMarkets || [],
        websiteUrl: response.data.websiteUrl || '',
        sustainableTravelSpecialization: response.data.sustainableTravelSpecialization || '',
        certifications: response.data.certifications || []
      })
       setAgreementStatus(response.data?.agreementAccepted || false);
    } catch (error) {
      console.error('Failed to load profile:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to load profile' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCertificationAdd = () => {
    if (newCertification.trim()) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }))
      setNewCertification('')
    }
  }

  const handleCertificationRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }))
  }

  const handleSpecializationAdd = () => {
    if (newSpecialization.trim()) {
      setFormData(prev => ({
        ...prev,
        specializations: [...prev.specializations, newSpecialization.trim()]
      }))
      setNewSpecialization('')
    }
  }

  const handleSpecializationRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.filter((_, i) => i !== index)
    }))
  }

  const handleTargetMarketAdd = () => {
    if (newTargetMarket.trim()) {
      setFormData(prev => ({
        ...prev,
        targetMarkets: [...prev.targetMarkets, newTargetMarket.trim()]
      }))
      setNewTargetMarket('')
    }
  }

  const handleTargetMarketRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      targetMarkets: prev.targetMarkets.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    setMessage({ type: '', text: '' })

    try {
      // Clean up the form data - remove empty strings
      const cleanedFormData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value !== '' && value !== null && value !== undefined) {
          // For arrays, only include if they have items
          if (Array.isArray(value)) {
            if (value.length > 0) {
              cleanedFormData[key] = value;
            }
          } else {
            cleanedFormData[key] = value;
          }
        }
      });

      
      const response = await travelAgentService.updateProfile(cleanedFormData)
      
      setProfile(response.data)
      setMessage({ type: 'success', text: 'Profile updated successfully!' })
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
    } catch (error) {
      console.error('Failed to update profile:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogout = () => {
    travelAgentService.logout()
    navigate('/login')
  }

  const handleProofOfBusinessUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB' })
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Only JPG, PNG, GIF, WEBP, and PDF are allowed' })
      return
    }

    setUploadingProof(true)
    setProofProgress(0)

    try {
      const response = await uploadService.uploadProofOfBusiness(file, (progress) => {
        setProofProgress(progress)
      })
      
      setFormData(prev => ({
        ...prev,
        proofOfBusinessUrl: response.data.fileUrl
      }))
      
      setMessage({ type: 'success', text: 'Proof of business uploaded successfully!' })
      
      // Reload profile to get updated data
      await loadProfile()
      
      setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
    } catch (error) {
      console.error('Upload error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to upload document' })
    } finally {
      setUploadingProof(false)
      setProofProgress(0)
      e.target.value = ''
    }
  }

  const handlePhotoIdUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB' })
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Only JPG, PNG, GIF, WEBP, and PDF are allowed' })
      return
    }

    setUploadingPhotoId(true)
    setPhotoIdProgress(0)

    try {
      const response = await uploadService.uploadPhotoId(file, (progress) => {
        setPhotoIdProgress(progress)
      })
      
      setFormData(prev => ({
        ...prev,
        photoIdUrl: response.data.fileUrl
      }))
      
      setMessage({ type: 'success', text: 'Photo ID uploaded successfully!' })
      
      // Reload profile to get updated data
      await loadProfile()
      
      setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
    } catch (error) {
      console.error('Upload error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to upload document' })
    } finally {
      setUploadingPhotoId(false)
      setPhotoIdProgress(0)
      e.target.value = ''
    }
  }

  const handleDeleteProofOfBusiness = async () => {
    if (!window.confirm('Are you sure you want to delete this document?')) return

    try {
      await uploadService.deleteProofOfBusiness()
      setFormData(prev => ({
        ...prev,
        proofOfBusinessUrl: ''
      }))
      setMessage({ type: 'success', text: 'Proof of business deleted successfully' })
      await loadProfile()
      setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
    } catch (error) {
      console.error('Delete error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to delete document' })
    }
  }

  const handleDeletePhotoId = async () => {
    if (!window.confirm('Are you sure you want to delete this document?')) return

    try {
      await uploadService.deletePhotoId()
      setFormData(prev => ({
        ...prev,
        photoIdUrl: ''
      }))
      setMessage({ type: 'success', text: 'Photo ID deleted successfully' })
      await loadProfile()
      setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
    } catch (error) {
      console.error('Delete error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to delete document' })
    }
  }

  if (isLoading) {
    return (
      <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="min-h-screen min-w-0 w-full overflow-x-hidden bg-slate-50/50">
        {/* Hero */}
        <section className="relative min-h-[20rem] sm:h-96 bg-black overflow-hidden">
          <div className="h-12 shrink-0" aria-hidden />
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
            style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30" />
          <div className="relative z-10 flex items-center min-h-[11rem] sm:h-[calc(24rem-3rem)] px-4 sm:px-6 lg:px-8 py-6 sm:py-0">
            <div className="max-w-7xl mx-auto w-full min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse shrink-0"></div>
                <p className="text-sm text-slate-300 font-medium tracking-wider uppercase truncate">Settings & Account</p>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl typography-hero-title-on-dark font-bold text-white mb-3 break-words">Reserve membership</h2>
              <p className="text-slate-300 text-base sm:text-lg max-w-2xl break-words">
                Update your personal and business details to maintain your partnership
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mt-8 sm:mt-10 pb-12 relative z-20 w-full min-w-0">
          <div className="max-w-4xl mx-auto w-full min-w-0">
            <Link
              to="/profile/reserve-membership-guide"
              className={`mb-8 block rounded-2xl border border-neutral-800 bg-black p-4 sm:p-6 shadow-md outline-none hover:border-white/25 hover:bg-neutral-950 focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black min-w-0 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transform transition-all duration-700`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-4 min-w-0">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
                    <BookOpen className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-semibold text-white break-words">Guide to your Reserve membership</h3>
                    <p className="mt-1 text-neutral-300 text-sm sm:text-base break-words">
                      Open the guide to view your welcome pack: what Reserve is, benefits, support, and expectations.
                    </p>
                  </div>
                </div>
                <span className="inline-flex flex-shrink-0 items-center gap-1 text-sm font-medium text-white sm:ml-auto">
                  View guide
                  <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                </span>
              </div>
            </Link>

            {/* Agreement Cards Section - Grid Layout */}
            <div className={`mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transform transition-all duration-700`}>
              {/* Travel Advisor Agreement Card */}
              <div className={`${profile?.agreementAccepted ? 'from-slate-50 to-slate-100/80 border-slate-200' : 'from-red-50 to-rose-50 border-red-200'} border bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-300 min-w-0`}>
                <div className="flex flex-col h-full">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className={`w-12 h-12 ${profile?.agreementAccepted ? 'bg-slate-100' : 'bg-red-100'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      {profile?.agreementAccepted ? (
                        <CheckCircle className="h-6 w-6 text-slate-700" />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-1 break-words">Travel Advisor Agreement</h3>
                      <p className="text-slate-600 text-sm sm:text-base break-words">
                        {profile?.agreementAccepted
                          ? `✓ Accepted on ${profile?.dateOfAcceptance ? new Date(profile.dateOfAcceptance).toLocaleDateString() : 'previously'}`
                          : 'Required for making bookings'}
                      </p>
                    </div>
                  </div>

                  {!profile?.agreementAccepted && (
                    <div className="mb-4 p-3 bg-red-100/50 border border-red-200 rounded-xl">
                      <div className="flex items-start space-x-2">
                        <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700">
                          Please review and accept our updated Terms of Service.
                        </p>
                      </div>
                    </div>
                  )}

       {profile?.agreementAccepted && (
                    <div className="mb-4 p-3 bg-slate-100/80 border border-slate-200 rounded-xl">
          <div className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-slate-700">
              You are already accepted the agreement.
            </p>
          </div>
        </div>
      )}
      
      <div className="mt-auto">
        <button
          onClick={handleAgreementButtonClick}
          className={`w-full flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
            profile?.agreementAccepted
            ? 'bg-slate-800 text-white hover:bg-slate-900 shadow-md hover:shadow-lg'
              : 'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
          }`}
        >
          <FileText className="h-5 w-5 mr-2" />
          {profile?.agreementAccepted ? 'View Agreement' : 'Review Agreement'}
        </button>
      </div>
    </div>
  </div>

  {/* General Terms Card */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100/80 border border-slate-200 rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-300 min-w-0">
    <div className="flex flex-col h-full">
      <div className="flex items-start space-x-4 mb-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-slate-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-1 break-words">General Terms & Conditions</h3>
          <p className="text-slate-600 text-sm sm:text-base break-words">
            Standard terms for all Axonall partners
          </p>
        </div>
      </div>
      
                  <div className="mb-4 p-3 bg-slate-100/80 border border-slate-200 rounded-xl">
        <div className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-slate-700">
            Applies to all travel advisors using our platform
          </p>
        </div>
      </div>
      
      <div className="mt-auto">
        <button
          onClick={handleGeneralTermsButtonClick}
                      className="w-full flex items-center justify-center px-6 py-3 rounded-xl font-medium bg-slate-800 text-white hover:bg-slate-900 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <FileText className="h-5 w-5 mr-2" />
          View General Terms
        </button>
      </div>
    </div>
  </div>
</div>

        <div className={`bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-8 shadow-sm ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transform transition-all duration-700 delay-100 min-w-0`}>
          <div className="flex items-start gap-3 mb-8 min-w-0">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
              <User className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
                  <h2 className="text-2xl sm:text-3xl typography-section-title font-bold text-slate-900 break-words">Reserve membership</h2>
              <p className="text-slate-600 text-sm sm:text-base break-words">Update your personal and business details</p>
            </div>
          </div>

          {/* Message */}
          {message.text && (
            <div className={`mb-8 p-4 sm:p-6 rounded-2xl ${
              message.type === 'success' 
                  ? 'bg-slate-50 border border-slate-200 text-slate-800' 
                : 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      message.type === 'success' ? 'bg-slate-200/80' : 'bg-red-100'
                }`}>
                  {message.type === 'success' ? (
                        <Award className="h-5 w-5 text-slate-700" />
                  ) : (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <span className="font-medium break-words min-w-0">{message.text}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8 min-w-0">
            {/* Personal Information */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-100 min-w-0">
              <div className="flex items-start gap-3 mb-6 min-w-0">
                    <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                  <User className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 break-words pt-1.5">Personal Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-3">
                    Full Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                          className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your full name"
                    />
                    <User className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-semibold text-slate-700 mb-3">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      required
                          className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your phone number"
                    />
                    <Phone className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-semibold text-slate-700 mb-3">
                    Country *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                          className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your country"
                    />
                    <MapPin className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
              </div>

              {/* KYC & Compliance Section */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-start gap-2 mb-4 min-w-0">
                  <Shield className="h-5 w-5 text-slate-600 shrink-0 mt-0.5" />
                  <h4 className="text-lg font-semibold text-slate-800 break-words min-w-0">KYC & Compliance Information</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
                  <div>
                    <label htmlFor="iataCliaArcNumber" className="block text-sm font-semibold text-slate-700 mb-3">
                      IATA / CLIA / ARC Number <span className="text-slate-500 font-normal">(Optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="iataCliaArcNumber"
                        name="iataCliaArcNumber"
                        value={formData.iataCliaArcNumber}
                        onChange={handleInputChange}
                            className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter IATA/CLIA/ARC number"
                      />
                      <Award className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="businessLicense" className="block text-sm font-semibold text-slate-700 mb-3">
                      Business License <span className="text-slate-500 font-normal">(Optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="businessLicense"
                        name="businessLicense"
                        value={formData.businessLicense}
                        onChange={handleInputChange}
                            className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter business license number"
                      />
                      <FileText className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-3 break-words">
                      Proof of Business <span className="text-slate-500 font-normal">(Upload business license, VAT certificate, or agency membership certificate)</span>
                    </label>
                    {formData.proofOfBusinessUrl ? (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-green-800 break-words">Document uploaded</p>
                              <p className="text-xs text-green-600 mt-1 break-words">Your proof of business has been securely stored</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleDeleteProofOfBusiness}
                            className="flex items-center justify-center sm:justify-start px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors shrink-0 self-end sm:self-center"
                          >
                            <Trash2 className="h-4 w-4 mr-2 shrink-0" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block w-full cursor-pointer">
                                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 hover:border-slate-500 hover:bg-slate-50 transition-all duration-200">
                            <div className="flex flex-col items-center justify-center space-y-3">
                                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                                      <Upload className="h-6 w-6 text-slate-600" />
                              </div>
                              <div className="text-center">
                                <p className="text-sm font-medium text-slate-700">
                                  {uploadingProof ? 'Uploading...' : 'Click to upload or drag and drop'}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF, WEBP or PDF (max. 5MB)</p>
                              </div>
                              {uploadingProof && (
                                <div className="w-full max-w-xs">
                                  <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div 
                                            className="bg-slate-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${proofProgress}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-xs text-center text-slate-600 mt-1">{proofProgress}%</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                            onChange={handleProofOfBusinessUpload}
                            disabled={uploadingProof}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-3 break-words">
                      Photo ID of Primary Contact <span className="text-slate-500 font-normal">(Passport or Driver's License for KYC compliance)</span>
                    </label>
                    {formData.photoIdUrl ? (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-green-800 break-words">Document uploaded</p>
                              <p className="text-xs text-green-600 mt-1 break-words">Your photo ID has been securely stored</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleDeletePhotoId}
                            className="flex items-center justify-center sm:justify-start px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors shrink-0 self-end sm:self-center"
                          >
                            <Trash2 className="h-4 w-4 mr-2 shrink-0" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block w-full cursor-pointer">
                                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 hover:border-slate-500 hover:bg-slate-50 transition-all duration-200">
                            <div className="flex flex-col items-center justify-center space-y-3">
                                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                                      <Upload className="h-6 w-6 text-slate-600" />
                              </div>
                              <div className="text-center">
                                <p className="text-sm font-medium text-slate-700">
                                  {uploadingPhotoId ? 'Uploading...' : 'Click to upload or drag and drop'}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF, WEBP or PDF (max. 5MB)</p>
                              </div>
                              {uploadingPhotoId && (
                                <div className="w-full max-w-xs">
                                  <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div 
                                            className="bg-slate-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${photoIdProgress}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-xs text-center text-slate-600 mt-1">{photoIdProgress}%</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                            onChange={handlePhotoIdUpload}
                            disabled={uploadingPhotoId}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-100 min-w-0">
              <div className="flex items-start gap-3 mb-6 min-w-0">
                    <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                  <Building className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 break-words pt-1.5">Business Information</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <label htmlFor="legalBusinessName" className="block text-sm font-semibold text-slate-700 mb-3">
                    Legal Business Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="legalBusinessName"
                      name="legalBusinessName"
                      value={formData.legalBusinessName}
                      onChange={handleInputChange}
                      required
                          className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter legal business name"
                    />
                    <Building className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
                  <div>
                    <label htmlFor="businessType" className="block text-sm font-semibold text-slate-700 mb-3">
                      Business Type *
                    </label>
                    <div className="relative">
                      <select
                        id="businessType"
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        required
                            className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200 appearance-none"
                      >
                        <option value="">Select business type</option>
                        <option value="sole">Sole Proprietor</option>
                        <option value="proprietor">Proprietor</option>
                        <option value="LLC/Ltd">LLC/Ltd</option>
                        <option value="partnership">Partnership</option>
                        <option value="Independent freelance">Independent Freelance</option>
                        <option value="corporate">Corporate</option>
                      </select>
                      <Building className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="businessRegistrationNumber" className="block text-sm font-semibold text-slate-700 mb-3">
                      Business Registration Number <span className="text-slate-500 font-normal">(if freelance type 'not applicable')</span>
                    </label>
                    <input
                      type="text"
                      id="businessRegistrationNumber"
                      name="businessRegistrationNumber"
                      value={formData.businessRegistrationNumber}
                      onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter registration number (if freelance type 'not applicable')"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
                  <div>
                    <label htmlFor="taxIdentificationNumber" className="block text-sm font-semibold text-slate-700 mb-3">
                      VAT / Tax ID <span className="text-slate-500 font-normal">(for invoicing and compliance)</span>
                    </label>
                    <input
                      type="text"
                      id="taxIdentificationNumber"
                      name="taxIdentificationNumber"
                      value={formData.taxIdentificationNumber}
                      onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter VAT/Tax ID number"
                    />
                  </div>
                  <div>
                    <label htmlFor="businessHours" className="block text-sm font-semibold text-slate-700 mb-3">
                      Business Hours
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="businessHours"
                        name="businessHours"
                        value={formData.businessHours}
                        onChange={handleInputChange}
                        placeholder="e.g., Mon-Fri 9AM-5PM"
                            className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      />
                      <Clock className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
                  <div>
                    <label htmlFor="businessPhoneNumber" className="block text-sm font-semibold text-slate-700 mb-3">
                      Business Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        id="businessPhoneNumber"
                        name="businessPhoneNumber"
                        value={formData.businessPhoneNumber}
                        onChange={handleInputChange}
                            className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter business phone"
                      />
                      <Phone className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="businessEmail" className="block text-sm font-semibold text-slate-700 mb-3">
                      Business Email
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="businessEmail"
                        name="businessEmail"
                        value={formData.businessEmail}
                        onChange={handleInputChange}
                            className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter business email"
                      />
                      <Mail className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="preferredContactMethod" className="block text-sm font-semibold text-slate-700 mb-3">
                    Preferred Contact Method
                  </label>
                  <select
                    id="preferredContactMethod"
                    name="preferredContactMethod"
                    value={formData.preferredContactMethod}
                    onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="businessAddress" className="block text-sm font-semibold text-slate-700 mb-3">
                    Business Address *
                  </label>
                  <textarea
                    id="businessAddress"
                    name="businessAddress"
                    value={formData.businessAddress}
                    onChange={handleInputChange}
                    required
                    rows={3}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your business address"
                  />
                </div>
                <div>
                  <label htmlFor="websiteUrl" className="block text-sm font-semibold text-slate-700 mb-3">
                    Website URL
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      id="websiteUrl"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleInputChange}
                      placeholder="https://yourwebsite.com"
                          className="w-full px-4 py-3 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    />
                    <Globe2 className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
              </div>
            </div>

            {/* Sustainability & Certifications */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-100 min-w-0">
              <div className="flex items-start gap-3 mb-6 min-w-0">
                    <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 break-words pt-1.5">Sustainability & Certifications</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <label htmlFor="sustainableTravelSpecialization" className="block text-sm font-semibold text-slate-700 mb-3">
                    Sustainable Travel Specialization *
                  </label>
                  <select
                    id="sustainableTravelSpecialization"
                    name="sustainableTravelSpecialization"
                    value={formData.sustainableTravelSpecialization}
                    onChange={handleInputChange}
                    required
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Planning to">Planning to</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Certifications
                  </label>
                  <div className="space-y-3">
                    {formData.certifications.map((cert, index) => (
                      <div key={index} className="flex items-start gap-2 bg-slate-50 rounded-xl p-3 min-w-0">
                        <span className="flex-1 min-w-0 text-slate-900 font-medium break-words">{cert}</span>
                        <button
                          type="button"
                          onClick={() => handleCertificationRemove(index)}
                          className="ml-auto shrink-0 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={newCertification}
                        onChange={(e) => setNewCertification(e.target.value)}
                        placeholder="Add certification"
                            className="min-w-0 flex-1 px-4 py-3 border border-slate-300 rounded-xl sm:rounded-r-none sm:rounded-l-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleCertificationAdd())}
                      />
                      <button
                        type="button"
                        onClick={handleCertificationAdd}
                            className="shrink-0 px-6 py-3 bg-slate-800 text-white rounded-xl sm:rounded-l-none sm:rounded-r-xl hover:bg-slate-900 transition-all duration-200 flex items-center justify-center w-full sm:w-auto"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Specializations & Target Markets */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-100 min-w-0">
              <div className="flex items-start gap-3 mb-6 min-w-0">
                    <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                  <Star className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 break-words pt-1.5">Specializations & Target Markets</h3>
              </div>
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Specializations
                  </label>
                  <div className="space-y-3">
                    {formData.specializations.map((spec, index) => (
                      <div key={index} className="flex items-start gap-2 bg-slate-50 rounded-xl p-3 min-w-0">
                        <span className="flex-1 min-w-0 text-slate-900 font-medium break-words">{spec}</span>
                        <button
                          type="button"
                          onClick={() => handleSpecializationRemove(index)}
                          className="ml-auto shrink-0 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={newSpecialization}
                        onChange={(e) => setNewSpecialization(e.target.value)}
                        placeholder="Add specialization (e.g., Luxury Travel, Adventure Tours)"
                            className="min-w-0 flex-1 px-4 py-3 border border-slate-300 rounded-xl sm:rounded-r-none sm:rounded-l-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSpecializationAdd())}
                      />
                      <button
                        type="button"
                        onClick={handleSpecializationAdd}
                            className="shrink-0 px-6 py-3 bg-slate-800 text-white rounded-xl sm:rounded-l-none sm:rounded-r-xl hover:bg-slate-900 transition-all duration-200 flex items-center justify-center w-full sm:w-auto"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Target Markets
                  </label>
                  <div className="space-y-3">
                    {formData.targetMarkets.map((market, index) => (
                      <div key={index} className="flex items-start gap-2 bg-slate-50 rounded-xl p-3 min-w-0">
                        <span className="flex-1 min-w-0 text-slate-900 font-medium break-words">{market}</span>
                        <button
                          type="button"
                          onClick={() => handleTargetMarketRemove(index)}
                          className="ml-auto shrink-0 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={newTargetMarket}
                        onChange={(e) => setNewTargetMarket(e.target.value)}
                        placeholder="Add target market (e.g., High Net Worth, Corporate, Leisure)"
                            className="min-w-0 flex-1 px-4 py-3 border border-slate-300 rounded-xl sm:rounded-r-none sm:rounded-l-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleTargetMarketAdd())}
                      />
                      <button
                        type="button"
                        onClick={handleTargetMarketAdd}
                            className="shrink-0 px-6 py-3 bg-slate-800 text-white rounded-xl sm:rounded-l-none sm:rounded-r-xl hover:bg-slate-900 transition-all duration-200 flex items-center justify-center w-full sm:w-auto"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row sm:justify-end pt-8 border-t border-slate-200">
              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary flex w-full sm:w-auto items-center justify-center px-8 py-4 text-base sm:text-lg"
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-3">Saving Changes...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-3" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
            </div>
        </div>
      </main>
      </div>


      <AgreementPopup
        isOpen={showAgreementPopup}
        onClose={() => setShowAgreementPopup(false)}
        profile={profile}
        onAgreementSubmit={handleAgreementSubmit}
        userData={formData}
      />

      <TermsPopup
        isOpen={showTermsPopup}
        onClose={() => setShowTermstPopup(false)}
        profile={profile}
        onAgreementSubmit={handleAgreementSubmit}
        userData={formData}
      />
    </MainLayout>
  )
}

export default ProfileManagementPage
